import { Component, ElementRef, OnInit, Input, ViewChild } from '@angular/core';
import { CfChartComponent } from '../chart/chart.component';
import { BarChartModel } from '../../models/chart/bar-chart.model';
import { BarChartTemplates } from '../../templates/bar-chart.template';
import { ChartStylingModel } from '../../models/chart/chart-styling.model';
import { TemplateService } from '../../services/template-service/template.service';
import { CfEventService } from '../../services/event-service/event.service';
/**
 * <p>CfBarChart component is created based on <b><a href="https://github.com/swimlane/ngx-charts" target="_blank">Ngx-charts</a></b>
 * and <b>CfChartComponet</b></p>
 * <code><pre>
 * import {{'{'}} BarChartModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} ChartStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-bar-chart<code>></code><code><</code><code>/</code>cf-bar-chart<code>></code>
 * </pre></code>
 * <code><pre>{{'{'}}
 * 	data: any[], 										// Data to show
 *  view: number[]                  // Dimensions of chart [width, height] by px
 * 	gradient: boolean, 							// Fill elements with a gradient instead of a solid color. Default: false.
 * 	showLegend: boolean, 						// If to show the legend. Default: true.
 * 	colorScheme: ChartsColorScheme, // Array with string css color values. Default: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA'].
 * 	showXAxis: boolean, 						// If to show the x axis. Default: true.
 * 	showYAxis: boolean, 						// If to show the y axis. Default: true.
 * 	showXAxisLabel: boolean, 				// If to show the x axis label. Default: true.
 * 	showYAxisLabel: boolean, 				// If to show the y axis label. Default: true.
 * 	xAxisLabel: string, 						// The x axis label
 * 	yAxisLabel: string, 						// The y axis label
 * 	orientation: string,						// Horizontal or vertical bar chart orientation. Default: vertical.
 * 	grouped: boolean,								// If to show bar chart in grouped mode. Default: false.
 * 	legendPosition: string,					// Legend position. Can be: top, right, bottom, left. Default: right.
 * 	chartAlignment: string,					// Alignment of chart. Can be: start, center, end. Default: start.
 * {{'}'}}</pre></code>
 * <p>Example of data object for not grouped bar chart:</p>
 * <code><pre>data = [
 *  {{'{'}} "name": "Germany", "value": 8940000 {{'}'}},
 *  {{'{'}} "name": "USA", "value": 5000000 {{'}'}},
 *  {{'{'}} "name": "France", "value": 7200000 {{'}'}}
 * ]</pre></code>
 * <p>Example of data object for grouped bar chart:</p>
 * <code><pre>data = [
 *  {{'{'}}
 *    "name": "Germany",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 7300000 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 8940000 {{'}'}}
 *    ]
 *  {{'}'}},
 *  {{'{'}}
 *    "name": "USA",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 7870000 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 8270000 {{'}'}}
 *    ]
 *  {{'}'}},
 *  {{'{'}}
 *    "name": "France",
 *    "series": [
 *    	{{'{'}} "name": "2010", "value": 5000002 {{'}'}},
 *    	{{'{'}} "name": "2011", "value": 5800000 {{'}'}}
 *     ]
 *   {{'}'}}
 * ]</pre></code>
 * <p>Example of chart options:</p>
 * <code><pre>myChartModel = new BarChartModel({{'{'}}
 * 	data: this.data,
 * 	orientation: 'horizontal',
 * 	grouped: true
 * {{'}'}});</pre></code>
 * <p>Styling</p>
 * <code><pre>{{'{'}}
 * 	barsContainerWidth: string, 							// It is css value for chart bars container width. Default: 100%.
 * 	barsContainerHeight: string', 						// It is css value for chart bars container height. Default: 200px.
 * 	container: StylingObject, 	              // Styling of chart container
 * {{'}'}}</pre></code>
 * <p>Example of styling object:</p>
 * <code><pre>chartStylingModel = new ChartStylingModel({{'{'}}
 * 	barsContainerWidth: '100%',
 * 	barsContainerHeight: '200px',
 * 	container: {{'{'}}
 * 		class: 'my-chart-class'
 * 	{{'}'}}
 * {{'}'}});</pre></code>
 */
export class CfBarChartComponent extends CfChartComponent implements OnInit {
/**
 * @hidden
 */
cfChartHost;
/**
 * @hidden
 */
cfNgxChart;
/**
 * <p>Horizontal or vertical bar chart orientation. Default: vertical.</p>
 * @param {?} value
 * @return {?}
 */
set orientation(value: string) { this.properties.orientation = value; }
/**
 * <p>Defines if the chart is grouped. Default: false.</p>
 * @param {?} value
 * @return {?}
 */
set grouped(value: boolean) { this.properties.grouped = value; }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} eventService
 */
constructor(public elementRef: ElementRef,
                /**@hidden */ templateService: TemplateService,
public eventService: CfEventService) {
    super(elementRef, templateService, eventService);
    this.properties = new BarChartModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit(): void {
    super.ngOnInit();
    this.getMyTemplate('bar-chart', BarChartTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'bar-chart').then( sampler => {
        if (this.properties.colorScheme.domain.length === 0) {
          this.properties.colorScheme = this.colorSchemes.find(s => s.name === this.properties.colorSchemeName);
        }
        this.styling = new ChartStylingModel(Object.assign({}, this.activeTemplate.style, this.styling));
        // console.log('bar-chart', this);
      });
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-bar-chart',
  template: "<div *ngIf=\"_inited && properties?.display\" [class]=\"styling?.container?.class\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <div #cfChartHost [style.height]=\"updateChartHeight(cfNgxChart?.chartElement?.nativeElement?.scrollHeight)\"> <cf-chart [style.width]=\"styling?.barsContainerWidth\" [style.height]=\"styling?.barsContainerHeight\" class=\"{{properties.legendPosition}} {{properties.chartAlignment}}\"> <ng-template #cfComponentChartTemplate> <ngx-charts-bar-vertical #cfNgxChart *ngIf=\"properties?.orientation == 'vertical' && !properties?.grouped\" class=\"cf-chart-zone\" [view]=\"properties?.view\" [ngStyle]=\"{'height': styling?.height}\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" (select)=\"onSelect($event)\"> </ngx-charts-bar-vertical> <ngx-charts-bar-horizontal #cfNgxChart *ngIf=\"properties?.orientation == 'horizontal' && !properties?.grouped\" class=\"cf-chart-zone\" [view]=\"properties?.view\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" (select)=\"onSelect($event)\"> </ngx-charts-bar-horizontal> <ngx-charts-bar-vertical-2d #cfNgxChart *ngIf=\"properties?.orientation == 'vertical' && properties?.grouped\" class=\"cf-chart-zone\" [view]=\"properties?.view\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" (select)=\"onSelect($event)\"> </ngx-charts-bar-vertical-2d> <ngx-charts-bar-horizontal-2d #cfNgxChart *ngIf=\"properties?.orientation == 'horizontal' && properties?.grouped\" class=\"cf-chart-zone\" [view]=\"properties?.view\" [scheme]=\"properties?.colorScheme\" [results]=\"properties?.data\" [gradient]=\"properties?.gradient\" [xAxis]=\"properties?.showXAxis\" [yAxis]=\"properties?.showYAxis\" [legend]=\"properties?.showLegend\" [showXAxisLabel]=\"properties?.showXAxisLabel\" [showYAxisLabel]=\"properties?.showYAxisLabel\" [xAxisLabel]=\"properties?.xAxisLabel\" [yAxisLabel]=\"properties?.yAxisLabel\" (select)=\"onSelect($event)\"> </ngx-charts-bar-horizontal-2d> </ng-template> </cf-chart> </div> </div> ",
  styles: [""]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: CfEventService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'cfChartHost': [{ type: ViewChild, args: ['cfChartHost', ] },],
'cfNgxChart': [{ type: ViewChild, args: ['cfNgxChart', ] },],
'orientation': [{ type: Input, args: ['orientation', ] },],
'grouped': [{ type: Input, args: ['grouped', ] },],
};
}

function CfBarChartComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfBarChartComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfBarChartComponent.ctorParameters;
/** @type {?} */
CfBarChartComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfBarChartComponent.prototype.cfChartHost;
/**
 * @hidden
 * @type {?}
 */
CfBarChartComponent.prototype.cfNgxChart;
/** @type {?} */
CfBarChartComponent.prototype.elementRef;
/**
 * @hidden
 * @type {?}
 */
CfBarChartComponent.prototype.eventService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
